import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/config";

/** Consentimiento de cookies — categorías y persistencia (D.L. 144 SV). */

export const COOKIE_CONSENT_KEY = "agdysa_cookie_consent";
export const COOKIE_POLICY_VERSION = "2026-09-10b";

export const COOKIE_CATEGORIES = {
  essential: {
    id: "essential",
    label: "Esenciales",
    description:
      "Necesarias para el funcionamiento del sitio (consentimiento, seguridad básica, sesión del panel).",
    required: true,
  },
  functional: {
    id: "functional",
    label: "Funcionales",
    description: "Mejoran la experiencia, p. ej. recordar el carrito de cotización.",
    required: false,
  },
  analytics: {
    id: "analytics",
    label: "Analítica de visitas",
    description:
      "Cuenta visitas totales y visitas por dirección IP para informes internos del CMS (sin Google Analytics).",
    required: false,
  },
  thirdParty: {
    id: "thirdParty",
    label: "Contenido de terceros",
    description:
      "YouTube, Facebook, Google Maps y catálogos Heyzine. Pueden establecer cookies ajenas a AGDYSA.",
    required: false,
  },
};

const DEFAULT_DENIED = {
  version: COOKIE_POLICY_VERSION,
  essential: true,
  functional: false,
  analytics: false,
  thirdParty: false,
  decided: false,
  updatedAt: null,
};

function emitChange(state) {
  try {
    window.dispatchEvent(new CustomEvent("agdysa:cookie-consent", { detail: state }));
  } catch {
    /* ignore */
  }
}

export function readCookieConsent() {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return { ...DEFAULT_DENIED };
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_DENIED,
      ...parsed,
      essential: true,
      analytics: Boolean(parsed.analytics),
      version: parsed.version || COOKIE_POLICY_VERSION,
    };
  } catch {
    return { ...DEFAULT_DENIED };
  }
}

export function writeCookieConsent(partial) {
  const next = {
    ...readCookieConsent(),
    ...partial,
    essential: true,
    decided: true,
    version: COOKIE_POLICY_VERSION,
    updatedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  emitChange(next);
  return next;
}

export function acceptAllCookies() {
  return writeCookieConsent({ functional: true, analytics: true, thirdParty: true });
}

export function rejectNonEssentialCookies() {
  return writeCookieConsent({ functional: false, analytics: false, thirdParty: false });
}

export function hasDecidedCookieConsent(state = readCookieConsent()) {
  return Boolean(state?.decided);
}

export function allowsThirdParty(state = readCookieConsent()) {
  return Boolean(state?.decided && state?.thirdParty);
}

export function allowsFunctional(state = readCookieConsent()) {
  if (!state?.decided) return true;
  return Boolean(state.functional);
}

export function allowsAnalytics(state = readCookieConsent()) {
  return Boolean(state?.decided && state?.analytics);
}

/**
 * Reporta la decisión del banner al servidor (agregado diario, sin IP).
 * Evita doble conteo en Strict Mode con sessionStorage.
 */
export function reportCookieConsentChoice(choice) {
  if (typeof window === "undefined") return;
  const allowed = ["accepted", "rejected", "customized"];
  if (!allowed.includes(choice)) return;
  try {
    const stamp = `${choice}:${Date.now()}`;
    const prev = sessionStorage.getItem("agdysa_consent_report");
    if (prev && prev.startsWith(`${choice}:`) && Date.now() - Number(prev.split(":")[1]) < 1500) {
      return;
    }
    sessionStorage.setItem("agdysa_consent_report", stamp);
  } catch {
    /* ignore */
  }
  try {
    const fn = httpsCallable(functions, "trackCookieConsentChoice");
    fn({ choice }).catch(() => {});
  } catch {
    /* silencioso */
  }
}
